import { useCampContext } from "~/contexts/CampProvider"
import { AutomatedMessageSchema, automatedMessagesFormDefaultValues, AutomatedMessageType } from "./validation"
import { useEffect, useState } from "react"
import { IAutomatedMessages } from "~/interfaces/AutomatedMessages"
import useAppForm from "~/hooks/useAppForm"
import useAutomatedMessages from "~/api2/hooks/useAutomatedMessages"

const CHECKIN_AUTOMATED_MESSAGES = "checkin"
const CHECKOUT_AUTOMATED_MESSAGES = "checkout"

export default function useAutomatedMessageForm() {
  const [defaultValues, setDefaultValues] = useState<AutomatedMessageType>(automatedMessagesFormDefaultValues)
  const [loadedDone, setLoadedDone] = useState(false)
  const { campId } = useCampContext()

  const reactHookForm = useAppForm<AutomatedMessageType>({
    defaultValues,
    schema: AutomatedMessageSchema,
  })

  const {
    index: {
      data: automatedMessages,
      refetch: refetchAutomatedMessages,
      isLoading: isLoadingAutomatedMessages,
      isFetching: isFetchingAutomatedMessages,
    },
    update: { mutate: saveAutomatedMessages, isPending: isPendingSaveAutomatedMessages },
  } = useAutomatedMessages()

  const isLoadingData = isLoadingAutomatedMessages || isFetchingAutomatedMessages || !automatedMessages

  const checkin = automatedMessages?.automated_message_templates?.find(
    (message) => message.name.toLowerCase() === CHECKIN_AUTOMATED_MESSAGES,
  )
  const checkout = automatedMessages?.automated_message_templates?.find(
    (message) => message.name.toLowerCase() === CHECKOUT_AUTOMATED_MESSAGES,
  )

  useEffect(() => {
    reactHookForm.reset(defaultValues)
    setLoadedDone((prev) => !prev)
  }, [defaultValues])

  useEffect(() => {
    handleSetDefaultValues()
  }, [automatedMessages, campId])

  const handleSetDefaultValues = () => {
    if (automatedMessages) {
      const newDefaults: AutomatedMessageType = {
        before_checkin_subject: checkin?.subject || "",
        before_checkin_content: checkin?.body || "",
        minutes_before_checkin:
          typeof checkin?.minutes_before_checkin === "number" ? checkin.minutes_before_checkin : null,
        before_checkin_cc_camp_notification_emails: !!checkin?.is_copying_camp_notification_emails,
        after_checkout_subject: checkout?.subject || "",
        after_checkout_content: checkout?.body || "",
        minutes_after_checkout:
          typeof checkout?.minutes_after_checkout === "number" ? checkout.minutes_after_checkout : null,
        after_checkout_cc_camp_notification_emails: !!checkout?.is_copying_camp_notification_emails,
      }
      setDefaultValues(newDefaults)
    }
  }

  const onSubmit = (data: AutomatedMessageType) => {
    const isCheckinFilled = data.before_checkin_subject?.length && data.before_checkin_content?.length
    const isCheckoutFilled = data.after_checkout_subject?.length && data.after_checkout_content?.length
    const automatedMessages: Record<"checkin" | "checkout", IAutomatedMessages> = {
      checkin: isCheckinFilled && {
        ...checkin,
        name: "checkin",
        subject: data.before_checkin_subject || "",
        body: data.before_checkin_content || "",
        minutes_before_checkin: data.minutes_before_checkin,
        is_copying_camp_notification_emails: data.before_checkin_cc_camp_notification_emails,
      },
      checkout: isCheckoutFilled && {
        ...checkout,
        name: "checkout",
        subject: data.after_checkout_subject || "",
        body: data.after_checkout_content || "",
        minutes_after_checkout: data.minutes_after_checkout,
        is_copying_camp_notification_emails: data.after_checkout_cc_camp_notification_emails,
      },
    }
    const newData = Object.values(automatedMessages).filter(Boolean) as IAutomatedMessages[]

    saveAutomatedMessages(
      {
        id: campId,
        value: newData,
      },
      {
        onSuccess: () => {
          setDefaultValues(data)
          refetchAutomatedMessages()
        },
      },
    )
  }
  return {
    onSubmit,
    reactHookForm,
    handleSetDefaultValues,
    isPendingSaveAutomatedMessages,
    isLoadingData,
    loadedDone,
  }
}
